/**
 * The student's "Kom igång" answers (slice C, P1-11; persona report §4), stored server-side in
 * `users.prefs` as JSON so the card does not come back on every new device. Pure: the server
 * validates a PATCH with zod and stamps `v` and `onboardedAt`; both sides read a stored value
 * through `parseUserPrefs`, which turns anything unreadable into `null` (= not onboarded).
 *
 * What the answers change:
 * - `goal`: the default difficulty on `/practice` (ruling 7: G/unknown = Lätt+Medel, VG =
 *   Medel+Svår) and the order of Today's new-topic problems (VG: Medel/Svår first).
 * - `minutesPerDay`: Today's defaults, cards 10/20/20 and new-topic problems 2/3/5.
 * - `position` + `missed`: missed lectures come first in Today's catch-up ("Ta igen F01").
 *   "reviewing" turns Tentaläge on (slice D, `shared/examPeriod.ts`): the Home card shows, the
 *   "Bara det vi gått igenom" default is off and "Plugga härnäst" may suggest untaught topics.
 * - `mathConfidence`: "unsure" adds a maths-support line on Today's card.
 *
 * Teachers see these answers only as aggregate counts (ruling 8), never per student.
 */

export const PREFS_VERSION = 1;

export const COURSE_POSITIONS = ["following", "missed", "reviewing"] as const;
export type CoursePosition = (typeof COURSE_POSITIONS)[number];

export const STUDY_GOALS = ["G", "VG", "unknown"] as const;
export type StudyGoal = (typeof STUDY_GOALS)[number];

export const MINUTES_PER_DAY = [10, 20, 30] as const;
export type MinutesPerDay = (typeof MINUTES_PER_DAY)[number];

export const MATH_CONFIDENCE = ["sure", "rusty", "unsure"] as const;
export type MathConfidence = (typeof MATH_CONFIDENCE)[number];

/** "saved": the student pressed Spara; "skipped": Hoppa över (the defaults are stored). */
export const ONBOARDING_OUTCOMES = ["saved", "skipped"] as const;
export type OnboardingOutcome = (typeof ONBOARDING_OUTCOMES)[number];

/** A session id of the course calendar (F01–F12, R01–R03). */
export const LECTURE_ID = /^[FR]\d{2}$/;
/** More missed sessions than the calendar holds cannot be meant. */
export const MAX_MISSED = 20;

/**
 * The topic Today's "Osäker" maths line links to ("Energiskalor, enheter och räkneverktyg").
 * `shared/__tests__/userPrefs.test.ts` checks it exists in `content/topics.json`.
 */
export const MATH_SUPPORT_TOPIC = "energy-units";

/**
 * The teacher overview shows the Kom igång breakdown only once this many students have saved
 * answers (ruling 8; review M1). Shared so the server's cut-off and the client's text agree.
 */
export const ONBOARDING_MIN_GROUP = 5;

/** What the client sends (`PATCH /api/auth/me { prefs }`). */
export interface UserPrefsInput {
  position: CoursePosition;
  /** Missed sessions; kept only with `position: "missed"`. */
  missed: string[];
  goal: StudyGoal;
  minutesPerDay: MinutesPerDay;
  mathConfidence: MathConfidence;
  outcome: OnboardingOutcome;
}

/** What is stored and returned on `/api/auth/me`. */
export interface UserPrefs extends UserPrefsInput {
  v: typeof PREFS_VERSION;
  /** When the card was first answered or skipped (ms). */
  onboardedAt: number;
}

/** The card's preselected answers (persona report §4), also what "Hoppa över" stores. */
export const DEFAULT_PREFS_INPUT: UserPrefsInput = {
  position: "following",
  missed: [],
  goal: "unknown",
  minutesPerDay: 20,
  mathConfidence: "rusty",
  outcome: "skipped",
};

function oneOf<T extends string | number>(list: readonly T[], value: unknown): value is T {
  return (list as readonly unknown[]).includes(value);
}

/** Missed ids as stored: well-formed, each once, only for `position: "missed"`. */
export function cleanMissed(position: CoursePosition, missed: readonly string[]): string[] {
  if (position !== "missed") return [];
  return [...new Set(missed.filter((id) => LECTURE_ID.test(id)))].slice(0, MAX_MISSED);
}

/** A stored value (JSON text already parsed, or the text itself); `null` when unreadable. */
export function parseUserPrefs(raw: unknown): UserPrefs | null {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const r = value as Record<string, unknown>;
  if (r.v !== PREFS_VERSION) return null;
  if (
    !oneOf(COURSE_POSITIONS, r.position) ||
    !oneOf(STUDY_GOALS, r.goal) ||
    !oneOf(MINUTES_PER_DAY, r.minutesPerDay) ||
    !oneOf(MATH_CONFIDENCE, r.mathConfidence) ||
    !oneOf(ONBOARDING_OUTCOMES, r.outcome) ||
    typeof r.onboardedAt !== "number" ||
    !Number.isFinite(r.onboardedAt)
  ) {
    return null;
  }
  const missed = Array.isArray(r.missed)
    ? r.missed.filter((id): id is string => typeof id === "string")
    : [];
  return {
    v: PREFS_VERSION,
    position: r.position,
    missed: cleanMissed(r.position, missed),
    goal: r.goal,
    minutesPerDay: r.minutesPerDay,
    mathConfidence: r.mathConfidence,
    outcome: r.outcome,
    onboardedAt: r.onboardedAt,
  };
}

/** The answers as the card shows them again ("Ändra mina val"), or the defaults. */
export function prefsInputOf(prefs: UserPrefs | null | undefined): UserPrefsInput {
  if (!prefs) return { ...DEFAULT_PREFS_INPUT, missed: [] };
  return {
    position: prefs.position,
    missed: [...prefs.missed],
    goal: prefs.goal,
    minutesPerDay: prefs.minutesPerDay,
    mathConfidence: prefs.mathConfidence,
    outcome: prefs.outcome,
  };
}

/** Ruling 7: the default difficulty filter on `/practice` (1 Lätt, 2 Medel, 3 Svår). */
export function difficultyForGoal(goal: StudyGoal | undefined): number[] {
  return goal === "VG" ? [2, 3] : [1, 2];
}

/** Today's defaults for the time a student has per day (persona report §4, question 3). */
export interface TodayDefaults {
  cardLimit: 10 | 20;
  newCount: number;
}

export function todayDefaultsFor(prefs: UserPrefs | null | undefined): TodayDefaults {
  switch (prefs?.minutesPerDay) {
    case 10:
      return { cardLimit: 10, newCount: 2 };
    case 30:
      return { cardLimit: 20, newCount: 5 };
    default:
      return { cardLimit: 20, newCount: 3 };
  }
}
